import { useCallback, useState } from 'react';

/**
 * Persists a record screen's sort selection (column + direction) for the duration
 * of the user's browser session, scoped per screen. Uses sessionStorage so the
 * chosen sort survives navigating away from and back to the screen within a
 * session, while resetting to the default on a fresh session.
 */
export interface SortPreference<TColumn, TDirection> {
  column: TColumn;
  direction: TDirection;
}

const STORAGE_KEY_PREFIX = 'sort-preference';

export function useSessionSortPreference<TColumn extends string | null, TDirection extends string | null>(
  screenName: string,
  defaultColumn: TColumn,
  defaultDirection: TDirection,
): [SortPreference<TColumn, TDirection>, (column: TColumn, direction: TDirection) => void] {
  const storageKey = `${STORAGE_KEY_PREFIX}-${screenName}`;

  const [preference, setPreference] = useState<SortPreference<TColumn, TDirection>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as SortPreference<TColumn, TDirection>;
        // Use `in` checks so a persisted `null` (a valid "no sort" state for
        // three-state sorts) is preserved rather than falling back to defaults.
        return {
          column: 'column' in parsed ? parsed.column : defaultColumn,
          direction: 'direction' in parsed ? parsed.direction : defaultDirection,
        };
      }
    } catch {
      // Ignore parse/access errors and fall back to defaults.
    }
    return { column: defaultColumn, direction: defaultDirection };
  });

  const persistPreference = useCallback(
    (column: TColumn, direction: TDirection) => {
      setPreference({ column, direction });
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ column, direction }));
      } catch {
        // Ignore quota/access errors; sort still works in-memory for this view.
      }
    },
    [storageKey],
  );

  return [preference, persistPreference];
}
